import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
    Unstable_Grid2 as Grid
} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../../../packages/query-builder/ui/components/form";
import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";


const ManageTechnicalPropertyTemplatesDialog = ({
                                                    open,
                                                    setOpen,
                                                    industryId,
                                                    templates,
                                                    selectedTemplateId,
                                                    selectedPropertyIds,
                                                    onTemplateUpserted
                                                }) => {
    const [replacing, setReplacing] = useState(false);
    const [creating, setCreating] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object({
        title: Yup.string()
            .required("this field is required")
            .trim(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Replaces the template with the current query.
     */
    const replaceTemplate = useCallback(async (template, selectedPropertyIds) => {
        const forApi = {
            propertiesId: selectedPropertyIds,
            templateId: template.id,
        }
        setReplacing(true);
        const response = await Api.setPropertiesForIndustryPropertyTemplate(forApi);
        if (!isMounted())
            return;
        setReplacing(false);
        if (response?.resultFlag) {
            onTemplateUpserted({
                id: template.id,
                title: template.title,
                selectedPropertyIds: forApi.propertiesId,
            })
            setOpen(false);
        }
    }, [isMounted, setOpen, onTemplateUpserted]);

    /**
     * Submits the form values to save a new template.
     */
    const createTemplate = useCallback(async (values, form) => {
        const forApi = {
            industryId: industryId,
            title: values.title,
        }
        setCreating(true);
        const response = await Api.createIndustryPropertyTemplate(forApi)
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag && !response?.aborted) {
            const template = {
                id: response.data.id,
                title: forApi.title,
                selectedPropertyIds: [],
            }
            onTemplateUpserted(template);
            void replaceTemplate(template, selectedPropertyIds);
            form.change('title', '')
            form.resetFieldState('title');
            setOpen(false);
        }
    }, [industryId, selectedPropertyIds, isMounted, onTemplateUpserted, setOpen, replaceTemplate]);

    /**
     * Creates the styles for the container boxes.
     * @param {Theme} theme                      The theme to use
     * @returns {ReturnType<ThemeMixin<Theme>>}  The styles for the container boxes
     */
    const boxMixin = useCallback((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: theme.vars?.shape.borderRadius,
        padding: 2,
        marginBlock: 2,
    }), []);


    return (
        <>
            <Dialog
                maxWidth={"sm"}
                fullWidth
                onClose={() => setOpen(false)}
                open={open}
            >
                <DialogTitle fontWeight={600}>
                    Save Template
                </DialogTitle>
                <DialogContent>
                    <Form
                        onSubmit={createTemplate}
                        validate={validate}
                    >
                        <Box sx={boxMixin}>
                            <Typography variant={'subtitle2'} fontWeight={600}>
                                Save a new template
                            </Typography>
                            <Grid container xs={12} gap={2} sx={{marginBlock: 2, marginInline: 0}}>
                                <Grid xs padding={0}>
                                    <MuiRFF.TextField
                                        fullWidth
                                        size={'small'}
                                        name={"title"}
                                        label={"Title"}
                                        required={required.title}
                                    />
                                </Grid>
                                <Grid xs={'auto'} padding={0}>
                                    <Button
                                        variant={'contained'}
                                        type={'submit'}
                                        color={'primary'}
                                        size={'small'}
                                        disabled={creating}
                                        sx={{padding: '4px 10px'}}
                                    >
                                        {
                                            creating
                                                ? "Saving..."
                                                : "Save"
                                        }
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Form>
                    <Divider
                        orientation="horizontal"
                        variant="middle"
                        sx={{marginBlock: 2, '&:before, &:after': {borderTopStyle: 'dashed',}}}
                    >
                        <Typography variant={'body2'} fontWeight={600}>
                            OR Replace Existing Template
                        </Typography>
                    </Divider>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {
                            !templates.length &&
                            <Typography variant={'body2'} textAlign={'center'} sx={{marginBlock: 3}}>
                                No templates found.
                            </Typography>
                        }
                        {
                            templates.map((template) => (
                                <Card
                                    key={template.id}
                                    value={template.id}
                                    variant={'outlined'}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        padding: 2,
                                        ...(selectedTemplateId === template.id && {
                                            borderColor: 'primary.main',
                                        })
                                    }}
                                >
                                    <Typography variant={'body1'} fontWeight={500}>
                                        {template.title}
                                        {
                                            selectedTemplateId === template.id &&
                                            <Typography variant={'body2'} fontWeight={500} color={'secondary.main'}>
                                                Currently Selected
                                            </Typography>
                                        }
                                    </Typography>
                                    <Button
                                        size={'small'}
                                        variant={'text'}
                                        disabled={replacing}
                                        onClick={() => replaceTemplate(template, selectedPropertyIds)}
                                    >
                                        {
                                            replacing
                                                ? "Saving..."
                                                : "Save"
                                        }
                                    </Button>
                                </Card>
                            ))
                        }
                    </Box>
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Button
                        variant={'text'}
                        type={'button'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ManageTechnicalPropertyTemplatesDialog;
